<template>
    <div height="medium" width="large">
        <div>
            <h2 class="heading">{{ $t('modals.sidebarList.content.message') }}</h2>
        </div>
        <div class="patient-chatbox">
            <!-- Chat Header -->
            <div class="patient-chatbox__header">
                <div class="patient-chatbox__header-info">
                    <div class="patient-chatbox__avatar">
                        <common-icon name="user" size="small"></common-icon>
                    </div>
                    <div>
                        <h3 class="patient-chatbox__title">
                            {{ patientName }}
                        </h3>
                        <p class="patient-chatbox__subtitle">
                            {{ $t('modals.patient_profile.profile.detail_form.message_patient_tab.heading') }}
                        </p>
                    </div>
                </div>
                <button class="patient-chatbox__refresh-btn" title="Refresh messages" @click="fetchMessages">
                    <common-icon name="reschedule" size="small"></common-icon>
                </button>
            </div>

            <!-- Messages Container -->
            <div ref="messageContainer" class="patient-chatbox__messages">
                <div v-if="isLoading && !messages.length" class="patient-chatbox__state patient-chatbox__state--loading">
                    <p>{{ $t('modals.referral_chat_modal.loading_messages') }}</p>
                </div>

                <div v-else-if="errorMessage" class="patient-chatbox__state patient-chatbox__state--error">
                    <p>{{ errorMessage }}</p>
                    <button class="patient-chatbox__retry-btn" @click="fetchMessages">
                        {{ $t('elements.availability.retry') }}
                    </button>
                </div>

                <div v-else-if="!messages.length" class="patient-chatbox__state patient-chatbox__state--empty">
                    <common-icon name="chat" size="large"></common-icon>
                    <p>{{ $t('modals.patient_profile.profile.detail_form.message_patient_tab.no_message') }}</p>
                </div>

                <template v-else>
                    <div
                        v-for="(msg, index) in messages"
                        :key="msg.id || index"
                        :class="[
                            'patient-chatbox__bubble-wrapper',
                            isSentByCurrentUser(msg)
                                ? 'patient-chatbox__bubble-wrapper--sent'
                                : 'patient-chatbox__bubble-wrapper--received',
                        ]"
                    >
                        <div v-if="getSenderName(msg)" class="patient-chatbox__sender-name">
                            {{ getSenderName(msg) }}
                        </div>
                        <div class="patient-chatbox__bubble">
                            <div class="patient-chatbox__body" v-html="msg.body"></div>
                            <span class="patient-chatbox__timestamp">{{ formatTime(msg.createdDateTime) }}</span>
                        </div>
                    </div>
                </template>
            </div>

            <!-- Input Box -->
            <div class="patient-chatbox__footer">
                <div class="patient-chatbox__input-wrapper">
                    <textarea
                        v-model="newMessageText"
                        class="patient-chatbox__textarea"
                        :accesskey="$t('modals.patient_profile.profile.detail_form.message_patient_tab.textbox')"
                        rows="2"
                        :disabled="isSending"
                        @keydown.enter.exact.prevent="sendMessage"
                    ></textarea>
                    <button class="patient-chatbox__send-btn" :disabled="!canSend || isSending" @click="sendMessage">
                        <span v-if="isSending" class="patient-chatbox__spinner"></span>
                        <template v-else>
                            <common-icon name="send-message" size="small"></common-icon>
                            <span>{{ $t('common.microcopy.send') }}</span>
                        </template>
                    </button>
                </div>
                <div class="patient-chatbox__hint">
                    {{ $t('modals.patient_profile.profile.detail_form.message_patient_tab.press') }}
                    <strong>{{ $t('modals.patient_profile.profile.detail_form.message_patient_tab.enter') }}</strong>
                    {{ $t('modals.patient_profile.profile.detail_form.message_patient_tab.to_send') }}
                    <strong>{{ $t('modals.patient_profile.profile.detail_form.message_patient_tab.shift_enter') }}</strong>
                    {{ $t('modals.patient_profile.profile.detail_form.message_patient_tab.new_line') }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import dayjs from 'dayjs'
    import MessagesAPI from 'api/MessagesAPI'

    export default {
        props: {
            patientDetails: {
                type: Object,
                default: () => ({}),
            },
        },
        data() {
            return {
                messages: [],
                newMessageText: '',
                isLoading: false,
                isSending: false,
                errorMessage: '',
            }
        },
        computed: {
            patientId() {
                return this.$route.params.id || this.patientDetails?.id
            },
            userId() {
                return this.$auth?.getUser()?.id
            },
            practiceId() {
                return this.$auth?.getUser()?.practiceId
            },
            patientName() {
                if (this.patientDetails?.firstName || this.patientDetails?.lastName) {
                    return `${this.patientDetails.firstName || ''} ${this.patientDetails.lastName || ''}`.trim()
                }
                return ''
            },
            canSend() {
                return this.newMessageText && this.newMessageText.trim().length > 0
            },
        },
        methods: {
            fetchMessages() {
                if (!this.patientId) return
                this.isLoading = true
                this.errorMessage = ''

                const query = {
                    patientId: Number(this.patientId),
                    objectType: 'direct',
                    order: 'desc',
                    userId: this.userId,
                }

                MessagesAPI.getMessages(query)
                    .then((res) => {
                        const list = Array.isArray(res) ? [...res] : []
                        this.messages = list.reverse()
                        this.scrollToBottom()
                    })
                    .catch(() => {
                        this.errorMessage = 'Failed to load message history. Please try again.'
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },
            sendMessage() {
                if (!this.canSend || this.isSending) return

                const textToSend = this.newMessageText.trim()
                this.isSending = true

                const messagePayload = {
                    fromUserId: this.userId,
                    objectType: 'direct',
                    body: textToSend,
                    fromPracticeId: this.practiceId,
                    toPatientId: Number(this.patientId),
                }

                MessagesAPI.sendMessage(messagePayload)
                    .then(() => {
                        this.newMessageText = ''
                        return this.fetchMessages()
                    })
                    .catch(() => {
                        this.errorMessage = 'Failed to send message. Please try again.'
                    })
                    .finally(() => {
                        this.isSending = false
                    })
            },
            isSentByCurrentUser(msg) {
                if (msg.fromUser && msg.fromUser.id) {
                    return msg.fromUser.id === this.userId
                }
                return msg.fromUserId === this.userId || !msg.toPatientId
            },
            getSenderName(msg) {
                if (this.isSentByCurrentUser(msg)) {
                    return 'You'
                }
                if (msg.fromUser) {
                    return `${msg.fromUser.firstName || ''} ${msg.fromUser.lastName || ''}`.trim() || 'Staff'
                }
                return this.patientName || 'Patient'
            },
            formatTime(dateTime) {
                if (!dateTime) return ''
                return dayjs(dateTime).format('MMM DD, YYYY h:mm A')
            },
            scrollToBottom() {
                this.$nextTick(() => {
                    const container = this.$refs.messageContainer
                    if (container) {
                        container.scrollTop = container.scrollHeight
                        setTimeout(() => {
                            if (container) {
                                container.scrollTop = container.scrollHeight
                            }
                        }, 50)
                    }
                })
            },
        },
        mounted() {
            this.fetchMessages()
        },
    }
</script>

<style lang="scss" scoped>
    .heading {
        font-size: 16px;
        color: oklch(0.21 0.034 264.665);
        font-weight: 500;
        margin-bottom: 14px;
    }
    .patient-chatbox {
        display: flex;
        flex-direction: column;
        height: 600px;
        background: #ffffff;
        border: 1px solid #ddd;
        border-radius: 12px;
        overflow: hidden;

        &__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 16px 20px;
            background: #fff;
            border-bottom: 1px solid #eceff3;
        }

        &__header-info {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        &__avatar {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: #eff6ff;
            color: #1447e6;
        }

        &__title {
            margin: 0;
            font-size: 16px;
            font-weight: 600;
            color: #111827;
        }

        &__subtitle {
            margin: 2px 0 0 0;
            font-size: 12px;
            color: #6b7280;
        }

        &__refresh-btn {
            background: transparent;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 6px 10px;
            cursor: pointer;
            color: #4b5563;
            transition: all 0.2s ease;

            &:hover {
                background: #f3f4f6;
                color: #111827;
            }
        }

        &__messages {
            flex: 1;
            padding: 20px;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            gap: 14px;
            background: #fcfcfd;
        }

        &__state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;
            color: #6b7280;
            gap: 8px;
            text-align: center;

            &--loading {
                color: #9ca3af;
            }

            &--error {
                color: #ef4444;
            }
        }

        &__retry-btn {
            margin-top: 8px;
            padding: 6px 16px;
            background: #ef4444;
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 13px;
        }

        &__bubble-wrapper {
            display: flex;
            flex-direction: column;
            max-width: 75%;

            &--sent {
                align-self: flex-end;
                align-items: flex-end;

                .patient-chatbox__sender-name {
                    color: #2563eb;
                }

                .patient-chatbox__bubble {
                    background: #1447e6;
                    color: #ffffff;
                    border-bottom-right-radius: 4px;

                    .patient-chatbox__timestamp {
                        color: rgba(255, 255, 255, 0.75);
                    }
                }
            }

            &--received {
                align-self: flex-start;
                align-items: flex-start;

                .patient-chatbox__sender-name {
                    color: #4b5563;
                }

                .patient-chatbox__bubble {
                    background: #f3f4f6;
                    color: #1f2937;
                    border-bottom-left-radius: 4px;

                    .patient-chatbox__timestamp {
                        color: #9ca3af;
                    }
                }
            }
        }

        &__sender-name {
            font-size: 11px;
            font-weight: 600;
            margin-bottom: 3px;
            padding: 0 4px;
        }

        &__bubble {
            padding: 10px 14px;
            border-radius: 16px;
            word-break: break-word;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        &__body {
            font-size: 14px;
            line-height: 1.45;
        }

        &__timestamp {
            display: block;
            font-size: 10px;
            margin-top: 4px;
            text-align: right;
        }

        &__footer {
            padding: 16px;
            background: #ffffff;
            border-top: 1px solid #eceff3;
        }

        &__input-wrapper {
            display: flex;
            gap: 12px;
            align-items: center;
        }

        &__textarea {
            flex: 1;
            padding: 10px 14px;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            font-family: inherit;
            font-size: 14px;
            resize: none;
            outline: none;
            transition: border-color 0.2s ease;

            &:focus {
                border-color: #2563eb;
                box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
            }

            &:disabled {
                background: #f9fafb;
            }
        }

        &__send-btn {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 10px 18px;
            background: #1447e6;
            color: #ffffff;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: background-color 0.2s ease;
            height: 42px;

            &:hover:not(:disabled) {
                background: #1d4ed8;
            }

            &:disabled {
                background: #9ca3af;
                cursor: not-allowed;
            }
        }

        &__spinner {
            width: 16px;
            height: 16px;
            border: 2px solid rgba(255, 255, 255, 0.3);
            border-radius: 50%;
            border-top-color: white;
            animation: spin 0.8s linear infinite;
        }

        &__hint {
            font-size: 11px;
            color: #9ca3af;
            margin-top: 6px;
        }
    }

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
</style>
